import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import type { DatasetFixedFilter, FixedFilterOp } from 'src/components/star-grid/datasets/datasetTypes';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';
import { LIST_OPS, valuesFor } from 'src/components/star-grid/datasets/utils/fixedFilterOps';

const OP_TEXT: Record<FixedFilterOp, Parameters<StarGridText>[0]> = {
  eq: 'wizardFixedFilterEq',
  neq: 'wizardFixedFilterNeq',
  in: 'wizardFixedFilterIn',
  not_in: 'wizardFixedFilterNotIn',
  gt: 'wizardFixedFilterGt',
  gte: 'wizardFixedFilterGte',
  lt: 'wizardFixedFilterLt',
  lte: 'wizardFixedFilterLte',
  between: 'wizardFixedFilterBetween'
};
const OPS = Object.keys(OP_TEXT) as FixedFilterOp[];

/** Predicates every query on the dataset applies, e.g. `pnl_job_id_partition = 'product_allocation_non_corp'`. */
export const FixedFiltersEditor: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const columnOptions = state.sourceColumns.map((column) => ({ value: column.name, label: `${column.name} (${column.type})` }));
  const opOptions = OPS.map((op) => ({ value: op, label: text(OP_TEXT[op]) }));
  const update = (index: number, filter: DatasetFixedFilter, patch: Partial<DatasetFixedFilter>): void =>
    state.updateFixedFilter(index, { ...filter, ...patch });

  return (
    <SpaceBetween size="xs">
      <Box variant="h4">{text('wizardFixedFilters')}</Box>
      <p className={styles.help}>{text('wizardFixedFiltersHelp')}</p>
      {state.fixedFilters.map((filter, index) => (
        // Filters have no id of their own; their position is their identity while editing.
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className={styles.lookupForm} data-testid={`fixed-filter-${index}`}>
          <FormField label={text('wizardFixedFilterColumn')}>
            <Select
              ariaLabel={text('wizardFixedFilterColumn')}
              data-testid={`fixed-filter-column-${index}`}
              filteringType="auto"
              statusType={state.sourceColumnsLoading ? 'loading' : 'finished'}
              options={columnOptions}
              selectedOption={columnOptions.find((option) => option.value === filter.column) ?? null}
              onChange={({ detail }) => update(index, filter, { column: detail.selectedOption.value ?? '' })}
            />
          </FormField>
          <FormField label={text('wizardFixedFilterOp')}>
            <Select
              ariaLabel={text('wizardFixedFilterOp')}
              data-testid={`fixed-filter-op-${index}`}
              options={opOptions}
              selectedOption={opOptions.find((option) => option.value === filter.op) ?? null}
              onChange={({ detail }) => {
                const op = detail.selectedOption.value as FixedFilterOp;
                update(index, filter, { op, values: valuesFor(op, filter.values) });
              }}
            />
          </FormField>
          {filter.op === 'between' ? (
            <SpaceBetween direction="horizontal" size="xs">
              <FormField label={text('wizardFixedFilterLow')}>
                <Input
                  value={filter.values[0] ?? ''}
                  data-testid={`fixed-filter-low-${index}`}
                  onChange={({ detail }) => update(index, filter, { values: [detail.value, filter.values[1] ?? ''] })}
                />
              </FormField>
              <FormField label={text('wizardFixedFilterHigh')}>
                <Input
                  value={filter.values[1] ?? ''}
                  data-testid={`fixed-filter-high-${index}`}
                  onChange={({ detail }) => update(index, filter, { values: [filter.values[0] ?? '', detail.value] })}
                />
              </FormField>
            </SpaceBetween>
          ) : (
            <FormField label={text('wizardFixedFilterValue')} description={LIST_OPS.has(filter.op) ? text('wizardFixedFilterInHelp') : undefined}>
              <Input
                value={LIST_OPS.has(filter.op) ? filter.values.join(',') : filter.values[0] ?? ''}
                data-testid={`fixed-filter-value-${index}`}
                // Split raw, not trimmed, so a trailing comma survives while the next value is typed; saving trims.
                onChange={({ detail }) => update(index, filter, { values: LIST_OPS.has(filter.op) ? detail.value.split(',') : [detail.value] })}
              />
            </FormField>
          )}
          <Button
            variant="icon"
            iconName="remove"
            ariaLabel={text('wizardFixedFilterRemove')}
            data-testid={`fixed-filter-remove-${index}`}
            onClick={() => state.removeFixedFilter(index)}
          />
        </div>
      ))}
      <Box>
        <Button iconName="add-plus" data-testid="fixed-filter-add" onClick={state.addFixedFilter}>
          {text('wizardFixedFilterAdd')}
        </Button>
      </Box>
    </SpaceBetween>
  );
};
